import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, Observable, shareReplay, Subscription, take} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

@Component({
  selector:
    'snelstart-venture-fiscaal-feature-box1-negatieve-persoonsgebonden-aftrek',
  templateUrl: './box1-negatieve-persoonsgebonden-aftrek.component.html',
  styleUrls: ['./box1-negatieve-persoonsgebonden-aftrek.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class Box1NegatievePersoonsgebondenAftrekComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;

  public fromGroup = new FormGroup({
    terugontvangenAlimentatie: new FormControl<number | null>(null),
    inkomstenSpecifiekeZiektekosten: new FormControl<number | null>(null),
    inkomstenOverigStudiekosten: new FormControl<number | null>(null),
    inkomstenOverigOnderhoudMonumentenpanden: new FormControl<number | null>(null),
    inkomstenOverigGiften: new FormControl<number | null>(null),
    inkomstenOverigTeruggekregenKwijtgescholdenDurfkapitaal: new FormControl<number | null>(null),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.fromGroup.reset({}, { emitEvent: false });
    this.fromGroup.patchValue(details.box1?.negatievePersoonsgebondenAftrek || {}, {emitEvent: false});
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      this.fromGroup.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.saveForm();
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  saveForm(): void {
    const form = this.fromGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          negatievePersoonsgebondenAftrek: {
            ...aangifte.box1?.negatievePersoonsgebondenAftrek,
            terugontvangenAlimentatie: form.terugontvangenAlimentatie ?? undefined,
            inkomstenSpecifiekeZiektekosten: form.inkomstenSpecifiekeZiektekosten ?? undefined,
            inkomstenOverigStudiekosten: form.inkomstenOverigStudiekosten ?? undefined,
            inkomstenOverigOnderhoudMonumentenpanden: form.inkomstenOverigOnderhoudMonumentenpanden ?? undefined,
            inkomstenOverigGiften: form.inkomstenOverigGiften ?? undefined,
            inkomstenOverigTeruggekregenKwijtgescholdenDurfkapitaal: form.inkomstenOverigTeruggekregenKwijtgescholdenDurfkapitaal ?? undefined,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
